import { Drawer, type PaperProps } from "@mui/material";
import { type ReactNode } from "react";
import { useResizableWidth } from "../../hooks/use-resizable-width";
import { ResizeHandle } from "./ResizeHandle";

interface ResizableDrawerProps {
	open: boolean;
	onClose: () => void;
	children: ReactNode;
	/** Initial width as % of viewport. */
	defaultWidthPct?: number;
	/** Drag clamp bounds (% of viewport). */
	minWidthPct?: number;
	maxWidthPct?: number;
	/** data-component label for the drawer paper. */
	dataComponent?: string;
	/** Raise above the modal layer — use when opened from inside a Dialog. */
	aboveModals?: boolean;
	id?: string;
}

/**
 * Right-anchored drawer whose width is set by dragging its left edge. The 8px
 * handle highlights in the primary colour on hover. Shared by every large
 * right-side detail panel (person, assignment, CV compare).
 */
export const ResizableDrawer = ({
	open,
	onClose,
	children,
	defaultWidthPct = 80,
	minWidthPct = 20,
	maxWidthPct = 90,
	dataComponent = "resizable-drawer",
	aboveModals = false,
	id,
}: ResizableDrawerProps) => {
	const { widthPct, startResize } = useResizableWidth(
		defaultWidthPct,
		minWidthPct,
		maxWidthPct,
		`drawer-width:${dataComponent}`
	);
	return (
		<Drawer
			id={id}
			open={open}
			onClose={onClose}
			anchor="right"
			sx={aboveModals ? { zIndex: (theme) => theme.zIndex.modal + 1 } : undefined}
			slotProps={{
				paper: {
					"data-component": dataComponent,
					sx: {
						width: `${widthPct}%`,
						background: (theme) => theme.palette.background.default,
					},
				} as PaperProps & { "data-component": string },
			}}
		>
			{/* Drag the left edge to resize the drawer. */}
			<ResizeHandle
				onPointerDown={startResize}
				cursor="ew-resize"
				absolute
				dataComponent="drawer-resize-handle"
			/>
			{children}
		</Drawer>
	);
};
